import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { NavItem } from '../../types';
import { usePermission } from '../../hooks/usePermissions';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/image-removebg-preview (4).png';

// Navigation items with translations
const getBaseNavigation = (t: (key: string) => string): NavItem[] => [
  { name: t('dashboard'), href: '/dashboard', icon: '📊' },
  { name: t('clients'), href: '/clients', icon: '👥' },
  { name: t('treatments'), href: '/treatments', icon: '🏥' },
  { name: t('treatmentTypes'), href: '/treatment-types', icon: '🎯' },
  { name: t('reports'), href: '/reports', icon: '📈' },
  { name: t('team'), href: '/team', icon: '👨‍💼' },
  { name: t('settings'), href: '/settings', icon: '⚙️' },
  { name: 'Documentation', href: '/documentation', icon: '📚' },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export function Sidebar({ isOpen, onClose, isExpanded, onToggleExpanded }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManageLocations = usePermission('canManageLocations');
  
  const baseNavigation = getBaseNavigation(t);
  
  const navigation = [
    ...baseNavigation.slice(0, 4), // Dashboard, Clients, Treatments, Treatment Types
    ...(canManageLocations ? [{ name: t('locations'), href: '/locations', icon: '📍' }] : []),
    ...baseNavigation.slice(4), // Reports, Team, Settings, Documentation
  ];

  // Admin navigation for Klijentli admins
  const adminNavigation: NavItem[] = user?.isKlijentliAdmin ? [
    { name: 'Admin Dashboard', href: '/admin', icon: '🛡️' },
    { name: 'Organizations', href: '/admin/organizations', icon: '🏢' },
    { name: 'Tickets', href: '/admin/tickets', icon: '🎫' },
  ] : [];

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 bg-white/95 backdrop-blur-lg shadow-2xl transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 border-r border-gray-200/50',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isExpanded ? 'w-64' : 'w-20'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo Toggle Button */}
          <div className="flex items-center justify-center h-20 border-b border-gray-200/50">
            <button
              onClick={onToggleExpanded}
              className="relative bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105"
              aria-label="Toggle sidebar"
            >
              <img src={logo} alt="Klijentli" className="h-10 w-auto" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-8 space-y-3 overflow-y-auto">
            {navigation.map((item, index) => (
              <NavLink
                key={item.name}
                to={item.href}
                title={item.name}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 animate-slide-up',
                    isExpanded ? 'justify-start' : 'justify-center',
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/25'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 hover:text-gray-900 hover:shadow-md'
                  )
                }
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={onClose}
              >
                <span className={cn(
                  "text-2xl transition-transform duration-200",
                  "group-hover:scale-110",
                  isExpanded ? "mr-3" : ""
                )}>{item.icon}</span>
                {isExpanded && (
                  <span className="whitespace-nowrap overflow-hidden transition-all duration-300">
                    {item.name}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Admin Section Separator */}
            {adminNavigation.length > 0 && (
              <>
                <div className={cn("border-t border-gray-200/50 my-4", isExpanded ? "mx-3" : "mx-1")} />
                <div className={cn("px-3 text-xs font-semibold text-gray-400 uppercase", !isExpanded && "hidden")}>
                  Admin
                </div>
                {adminNavigation.map((item, index) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    title={item.name}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200',
                        isExpanded ? 'justify-start' : 'justify-center',
                        isActive
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25'
                          : 'text-gray-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 hover:text-gray-900 hover:shadow-md'
                      )
                    }
                    onClick={onClose}
                  >
                    <span className={cn(
                      "text-2xl transition-transform duration-200",
                      "group-hover:scale-110",
                      isExpanded ? "mr-3" : ""
                    )}>{item.icon}</span>
                    {isExpanded && (
                      <span className="whitespace-nowrap overflow-hidden transition-all duration-300">
                        {item.name}
                      </span>
                    )}
                  </NavLink>
                ))}
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200/50">
            <div className="text-center">
              <div className="text-xs text-gray-400 transform rotate-90 whitespace-nowrap">v1.0</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
